import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, Instagram } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import WhatsAppButton from './WhatsAppButton';

const Layout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: '/', label: t('nav.home') },
    { path: '/le-riad', label: t('nav.riad') },
    { path: '/chambres', label: t('nav.rooms') },
    { path: '/espaces', label: t('nav.spaces') },
    { path: '/services', label: t('nav.services') },
    { path: '/localisation', label: t('nav.location') },
    { path: '/disponibilites', label: t('nav.availability') }
  ];

  const toggleLanguage = () => {
    const newLang = i18n.language === 'fr' ? 'en' : 'fr';
    i18n.changeLanguage(newLang);
  };

  const isHomePage = location.pathname === '/';

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <nav
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
          scrolled 
            ? 'glass-warm shadow-lg' 
            : isHomePage 
              ? 'bg-transparent' 
              : 'glass-warm'
        }`}
      >
        <div className="px-8 md:px-16 lg:px-32 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" data-testid="logo-link" className="flex items-center gap-4">
              <img 
                src="/images/gkp10dgt_logomenu.png" 
                alt="Riad KAMA" 
                className="h-14 md:h-16 w-auto"
              />
            </Link>

            <div className="hidden lg:flex items-center gap-12">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={`text-xs uppercase tracking-[0.2em] transition-all duration-500 relative group font-medium ${
                    location.pathname === item.path
                      ? 'text-[#D4AF37]'
                      : scrolled || !isHomePage
                        ? 'text-[#1A1A1A] hover:text-[#B8860B]'
                        : 'text-white hover:text-[#D4AF37]'
                  }`}
                  style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '500' }}
                >
                  {item.label}
                  <span className={`absolute bottom-0 left-0 w-0 h-[1px] bg-[#D4AF37] transition-all duration-500 group-hover:w-full ${
                    location.pathname === item.path ? 'w-full' : ''
                  }`}></span>
                </Link>
              ))}
              <button
                onClick={toggleLanguage}
                data-testid="language-toggle"
                className={`text-xs uppercase tracking-[0.2em] transition-all duration-500 px-6 py-3 border-2 border-[#B8860B] font-medium ${
                  scrolled || !isHomePage
                    ? 'text-[#B8860B] hover:bg-[#B8860B] hover:text-white'
                    : 'text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white'
                }`}
                style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '500' }}
              >
                {i18n.language === 'fr' ? 'EN' : 'FR'}
              </button>
            </div>

            <button
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="mobile-menu-button"
              className={`lg:hidden ${
                scrolled || !isHomePage ? 'text-[#1A1A1A]' : 'text-white'
              }`}
            >
              {menuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5 }}
              data-testid="mobile-menu"
              className="lg:hidden bg-[#FAF8F5] border-t border-[#D4AF37]/20 fixed left-0 right-0 top-[80px] z-40 shadow-lg"
            >
              <div className="px-8 py-8 space-y-5">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMenuOpen(false)}
                    className={`block text-sm uppercase tracking-[0.2em] transition-colors duration-300 font-medium ${
                      location.pathname === item.path
                        ? 'text-[#B8860B]'
                        : 'text-[#1A1A1A] hover:text-[#B8860B]'
                    }`}
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {item.label}
                  </Link>
                ))}
                <button
                  onClick={toggleLanguage}
                  className="text-sm uppercase tracking-[0.2em] border border-[#D4AF37] text-[#D4AF37] px-6 py-3 hover:bg-[#D4AF37] hover:text-white transition-all duration-300"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {i18n.language === 'fr' ? 'EN' : 'FR'}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <main>{children}</main>

      <footer data-testid="main-footer" className="bg-[#0A0A0A] text-white py-24 px-8 md:px-16 lg:px-32 border-t border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 mb-16">
            <div className="lg:col-span-1">
              <img 
                src="/images/gkp10dgt_logomenu.png" 
                alt="Riad KAMA" 
                className="h-20 w-auto mb-6"
              />
              <p className="text-sm text-white/70 leading-relaxed mb-6" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '300' }}>
                Riad de luxe privatisé au cœur de la Kasbah. Une expérience exclusive où l'art de vivre marocain rencontre le raffinement contemporain.
              </p>
              <div className="flex items-center gap-4">
                <a
                  href="https://instagram.com/riad_kama_marrakech"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="instagram-link"
                  className="text-[#D4AF37] hover:text-white transition-colors duration-300"
                >
                  <Instagram size={24} />
                </a>
              </div>
            </div>

            <div>
              <h4
                className="text-sm uppercase tracking-[0.2em] mb-6 text-[#D4AF37] font-semibold"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Contact
              </h4>
              <div className="space-y-4 text-sm text-white/80" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '300' }}>
                <div>
                  <p className="text-white font-medium mb-1">Adresse</p>
                  <p>8 Derb Riad Mechouar El Kasbah</p>
                  <p>Kasbah, Médina de Marrakech</p>
                  <p>Maroc</p>
                </div>
                <div>
                  <p className="text-white font-medium mb-1">WhatsApp</p>
                  <a href="https://wa.me/33670133353" className="text-[#D4AF37] hover:text-white transition-colors">
                    +33 6 70 13 33 53
                  </a>
                </div>
                <div>
                  <p className="text-white font-medium mb-1">Instagram</p>
                  <a href="https://instagram.com/riad_kama_marrakech" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:text-white transition-colors">
                    @riad_kama_marrakech
                  </a>
                </div>
              </div>
            </div>

            <div>
              <h4
                className="text-sm uppercase tracking-[0.2em] mb-6 text-[#D4AF37] font-semibold"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Navigation
              </h4>
              <div className="space-y-3">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="block text-sm text-white/80 hover:text-[#D4AF37] transition-colors duration-300"
                    style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '300' }}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <h4
                className="text-sm uppercase tracking-[0.2em] mb-6 text-[#D4AF37] font-semibold"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Informations
              </h4>
              <div className="space-y-4 text-sm text-white/80" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '300' }}>
                <div>
                  <p className="text-white font-medium mb-1">Capacité</p>
                  <p>10 personnes maximum</p>
                  <p>5 chambres élégantes</p>
                </div>
                <div>
                  <p className="text-white font-medium mb-1">Type</p>
                  <p>Privatisation exclusive</p>
                  <p>Location en totalité uniquement</p>
                </div>
                <div>
                  <p className="text-white font-medium mb-1">Services inclus</p>
                  <p>Petit-déjeuner • Gouvernante</p>
                  <p>Ménage quotidien • Wi-Fi</p>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/60" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '300' }}>
              {t('footer.legal')}
            </p>
            <p className="text-sm text-white/60" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '300' }}>
              Site créé avec soin pour refléter l'excellence du Riad KAMA
            </p>
          </div>
        </div>
      </footer>

      <WhatsAppButton />
    </div>
  );
};

export default Layout;
